import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Field,
  Input,
  LoadingState,
  Select,
  useToast,
} from '@/components/ui';
import { useBranch } from '@/features/branch/branch-context';
import {
  useMySchedule,
  useSubmitScheduleRequest,
  type StaffSchedule,
} from '@/features/schedules/api';
import { PageContainer } from '@/layouts/PageContainer';
import { isApiError } from '@/lib/api';
import styles from './MySchedulePage.module.css';

const WEEKDAYS = [1, 2, 3, 4, 5, 6, 0] as const;

function time(minutes: number) {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
}

function range(schedule: StaffSchedule) {
  return `${time(schedule.startMin)}–${time(schedule.endMin)}`;
}

function displayDate(date: string) {
  return new Intl.DateTimeFormat('en-MY', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(new Date(`${date}T00:00:00`));
}

export function MySchedulePage() {
  const { t } = useTranslation();
  const { branches, selected } = useBranch();
  const schedule = useMySchedule();
  const submit = useSubmitScheduleRequest();
  const toast = useToast();
  const [branchId, setBranchId] = useState(
    selected?.id ?? branches[0]?.id ?? '',
  );
  const [kind, setKind] = useState('');
  const [date, setDate] = useState('');
  const [details, setDetails] = useState('');
  const [clientError, setClientError] = useState(false);

  const branchName = (id: string) =>
    branches.find((branch) => branch.id === id)?.name ?? id;

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    setClientError(false);
    submit.reset();
    if (!branchId || !kind || !date || !details.trim()) {
      setClientError(true);
      return;
    }
    submit.mutate(
      { branchId, kind, date, text: details.trim() },
      {
        onSuccess: () => {
          setKind('');
          setDate('');
          setDetails('');
          toast.show({ tone: 'success', title: t('schedules.submitted') });
        },
      },
    );
  };

  const apiError = isApiError(submit.error) ? submit.error : undefined;
  const submitError = clientError
    ? t('schedules.validationError')
    : apiError
      ? apiError.code === 'VALIDATION_FAILED'
        ? t('schedules.validationError')
        : apiError.message
      : submit.error
        ? t('errors.genericBody')
        : null;

  return (
    <PageContainer>
      <div className={styles.stack}>
        <p className={styles.notice}>{t('schedules.notice')}</p>

        <Card title={t('schedules.weekly')}>
          {schedule.isLoading ? (
            <LoadingState />
          ) : schedule.error ? (
            <ErrorState
              error={schedule.error}
              onRetry={() => void schedule.refetch()}
            />
          ) : (
            <ul className={styles.week} aria-label={t('schedules.myTitle')}>
              {WEEKDAYS.map((weekday) => {
                const shifts =
                  schedule.data?.templates.filter(
                    (item) => item.weekday === weekday,
                  ) ?? [];
                return (
                  <li key={weekday} className={styles.day}>
                    <p className={styles.dayName}>
                      {t(`schedules.weekdays.${weekday}`)}
                    </p>
                    {shifts.length ? (
                      shifts.map((shift) => (
                        <div key={shift.id}>
                          <p className={styles.shift}>{range(shift)}</p>
                          <p className={styles.branch}>
                            {branchName(shift.branchId)}
                          </p>
                        </div>
                      ))
                    ) : (
                      <p className={styles.emptyShift}>
                        {t('schedules.noShift')}
                      </p>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </Card>

        <Card title={t('schedules.overrides')}>
          {schedule.data?.overrides.length ? (
            <ul className={styles.overrides}>
              {schedule.data.overrides.map((override) => (
                <li key={override.id} className={styles.override}>
                  <div>
                    <strong>{displayDate(override.date!)}</strong>
                    <p className={styles.branch}>
                      {branchName(override.branchId)}
                    </p>
                  </div>
                  <strong>{range(override)}</strong>
                </li>
              ))}
            </ul>
          ) : !schedule.isLoading && !schedule.error ? (
            <EmptyState
              title={t('schedules.noOverrides')}
              body={t('schedules.noOverridesBody')}
            />
          ) : null}
        </Card>

        {/* RL-1884: what was asked and what the manager decided. */}
        <Card title="My requests">
          {schedule.data?.requests?.length ? (
            <ul className={styles.requests}>
              {schedule.data.requests.map((r) => (
                <li key={r.id}>
                  <strong>
                    {t(`schedules.requestKinds.${r.kind}`)} ·{' '}
                    {displayDate(r.date)}
                  </strong>{' '}
                  <Badge
                    tone={
                      r.status === 'approved'
                        ? 'success'
                        : r.status === 'declined'
                          ? 'danger'
                          : 'warning'
                    }
                  >
                    {r.status === 'approved'
                      ? 'Approved'
                      : r.status === 'declined'
                        ? 'Declined'
                        : 'Waiting for the manager'}
                  </Badge>
                  <p>
                    {r.status === 'approved' && r.calendarEventId
                      ? 'In the clinic calendar; patients booked with you that day are asked to pick a new time.'
                      : r.decidedAt
                        ? `Decided ${new Date(r.decidedAt).toLocaleDateString('en-GB', { timeZone: 'Asia/Kuala_Lumpur' })}`
                        : r.text}
                  </p>
                </li>
              ))}
            </ul>
          ) : (
            <p className={styles.formIntro}>No requests yet.</p>
          )}
        </Card>

        <Card title={t('schedules.changeRequest')}>
          <p className={styles.formIntro}>{t('schedules.changeRequestBody')}</p>
          <form className={styles.form} onSubmit={onSubmit} noValidate>
            <Field label={t('schedules.branch')}>
              <Select
                value={branchId}
                onChange={(event) => setBranchId(event.target.value)}
                options={branches.map((branch) => ({
                  value: branch.id,
                  label: branch.name,
                }))}
              />
            </Field>
            <Field label={t('schedules.requestKind')}>
              <Select
                value={kind}
                onChange={(event) => setKind(event.target.value)}
                placeholder={t('schedules.requestKindPlaceholder')}
                options={['swap_shift', 'leave', 'hours_change', 'other'].map(
                  (value) => ({
                    value,
                    label: t(`schedules.requestKinds.${value}`),
                  }),
                )}
              />
            </Field>
            <Field label={t('schedules.date')}>
              <Input
                type="date"
                value={date}
                onChange={(event) => setDate(event.target.value)}
              />
            </Field>
            <div className={styles.details}>
              <Field label={t('schedules.details')}>
                <textarea
                  className={styles.textarea}
                  value={details}
                  onChange={(event) => setDetails(event.target.value)}
                  placeholder={t('schedules.detailsPlaceholder')}
                />
              </Field>
            </div>
            {submitError && (
              <ErrorState
                error={submit.error}
                title={t('schedules.submitFailed')}
                body={submitError}
              />
            )}
            <div className={styles.actions}>
              <Button
                type="submit"
                variant="primary"
                loading={submit.isPending}
              >
                {t('schedules.submit')}
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </PageContainer>
  );
}
